import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { OrganizationService } from '../services/organization.service';
import { AuthService } from '../services/auth.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { map, catchError, of, filter, take, switchMap } from 'rxjs';

/**
 * Guard to ensure only users with Inactive/Suspended organizations can access the account-inactive page
 * If organization is Active, redirect to home
 */
export const accountInactiveGuard: CanActivateFn = () => {
  const organizationService = inject(OrganizationService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const msalBroadcastService = inject(MsalBroadcastService);

  // Wait for MSAL to finish initialization before checking
  return msalBroadcastService.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    take(1),
    switchMap(() => {
      const user = authService.getUser();

      // If not authenticated at all, allow access to account-inactive page
      // (edge case: user was inactive and then logged out)
      if (!user?.localAccountId) {
        return of(true);
      }

      // Check organization status
      return organizationService
        .checkUserOrganization(user.localAccountId)
        .pipe(
          map((response) => {
            if (response?.data?.organizationId) {
              const orgStatus = response.data.status;

              // Only allow access to account-inactive page if status is NOT Active
              if (orgStatus && orgStatus !== 'Active') {
                return true;
              }

              // Organization is Active - redirect to home
              router.navigate(['/']);
              return false;
            }

            // No organization - redirect to signup
            router.navigate(['/onboarding/signup-process']);
            return false;
          }),
          catchError((error) => {
            console.error(
              'Error checking organization in account-inactive guard:',
              error,
            );
            // On error, allow access to account-inactive page (fail safe)
            return of(true);
          }),
        );
    }),
  );
};
